import React, { useState, useEffect } from 'react';
import Icon from './Icon';
import styles from './AddContactModal.module.css';

function capitaliseName(raw) {
  if (!raw) return '';
  return raw.trim().split(/\s+/)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

export default function AddContactModal({ message, senderInfo, onClose, onAdded }) {
  const isExistingCustomer = senderInfo?.matchType === 'customer';

  const [name,        setName]        = useState(capitaliseName(message?.fromName || message?.from?.split('@')[0]));
  const [email]                       = useState(message?.from || '');
  const [phone,       setPhone]       = useState('');
  const [companyName, setCompanyName] = useState(isExistingCustomer ? (senderInfo.customer?.customerName || '') : '');
  const [address1,    setAddress1]    = useState('');
  const [address2,    setAddress2]    = useState('');
  const [city,        setCity]        = useState('');
  const [county,      setCounty]      = useState('');
  const [postcode,    setPostcode]    = useState('');
  const [country,     setCountry]     = useState('');
  const [extracting,  setExtracting]  = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState(null);

  // Extract contact info from email body on open
  useEffect(() => {
    if (!message?.bodyText && !message?.bodyHtml) { setExtracting(false); return; }
    setExtracting(true);
    fetch('/api/mail/extract-contact-info', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ bodyText: message.bodyText, bodyHtml: message.bodyHtml }),
    })
      .then(r => r.json())
      .then(data => {
        if (data.companyName && !isExistingCustomer) setCompanyName(data.companyName);
        if (data.phone)    setPhone(data.phone);
        if (data.address1) setAddress1(data.address1);
        if (data.address2) setAddress2(data.address2);
        if (data.city)     setCity(data.city);
        if (data.county)   setCounty(data.county);
        if (data.postcode) setPostcode(data.postcode);
        if (data.country)  setCountry(data.country);
      })
      .catch(() => {})
      .finally(() => setExtracting(false));
  }, []);

  async function handleSave() {
    if (!name.trim()) { setError('Name is required'); return; }
    if (!isExistingCustomer && !companyName.trim()) { setError('Company name is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const body = {
        name:        capitaliseName(name),
        email:       email.trim().toLowerCase(),
        phone,
        companyName: companyName.trim(),
        address1, address2, city, county, postcode, country,
      };
      if (isExistingCustomer) body.customerId = senderInfo.customer.id;

      const res  = await fetch('/api/mail/add-contact', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      onAdded(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.titleBar}>
          <span className={styles.title}>Add to OnPoint</span>
          {extracting && <span className={styles.extracting}>Reading email…</span>}
          <button className={styles.closeBtn} onClick={onClose} title="Close">
            <Icon name="x" size={13} />
          </button>
        </div>

        <div className={styles.body}>
          {/* Contact */}
          <div className={styles.section}>
            <div className={styles.sectionLabel}>Contact</div>
            <div className={styles.row}>
              <div className={styles.field}>
                <label className={styles.label}>Full name</label>
                <input className={styles.input} value={name}
                  onChange={e => setName(e.target.value)}
                  onBlur={e => setName(capitaliseName(e.target.value))}
                  placeholder="First Last" autoFocus />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Phone</label>
                <input className={styles.input} value={phone}
                  onChange={e => setPhone(e.target.value)} placeholder="Optional" />
              </div>
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Email</label>
              <input className={styles.input} value={email} readOnly />
            </div>
          </div>

          {/* Company */}
          <div className={styles.section}>
            {isExistingCustomer ? (
              <>
                <div className={styles.sectionLabel}>Adding to existing customer</div>
                <div className={styles.customerBadge}>
                  <span className={styles.customerIcon}><Icon name="check" size={13} stroke={2.4} /></span>
                  <span className={styles.customerName}>{senderInfo.customer?.customerName}</span>
                </div>
              </>
            ) : (
              <>
                <div className={styles.sectionLabel}>New company</div>
                <div className={styles.field}>
                  <label className={styles.label}>Company name</label>
                  <input className={styles.input} value={companyName}
                    onChange={e => setCompanyName(e.target.value)} placeholder="Company name" />
                </div>
                <div className={styles.row}>
                  <div className={styles.field}>
                    <label className={styles.label}>Address line 1</label>
                    <input className={styles.input} value={address1}
                      onChange={e => setAddress1(e.target.value)} placeholder="" />
                  </div>
                  <div className={styles.field}>
                    <label className={styles.label}>Address line 2</label>
                    <input className={styles.input} value={address2}
                      onChange={e => setAddress2(e.target.value)} placeholder="" />
                  </div>
                </div>
                <div className={styles.row}>
                  <div className={styles.field}>
                    <label className={styles.label}>City</label>
                    <input className={styles.input} value={city}
                      onChange={e => setCity(e.target.value)} placeholder="" />
                  </div>
                  <div className={styles.field}>
                    <label className={styles.label}>County</label>
                    <input className={styles.input} value={county}
                      onChange={e => setCounty(e.target.value)} placeholder="" />
                  </div>
                </div>
                <div className={styles.row}>
                  <div className={styles.field}>
                    <label className={styles.label}>Postcode</label>
                    <input className={styles.input} value={postcode}
                      onChange={e => setPostcode(e.target.value)} placeholder="" />
                  </div>
                  <div className={styles.field}>
                    <label className={styles.label}>Country</label>
                    <input className={styles.input} value={country}
                      onChange={e => setCountry(e.target.value)} placeholder="" />
                  </div>
                </div>
              </>
            )}
          </div>

          <div className={styles.defaults}>
            <span className={styles.defaultItem}>
              <Icon name="check" size={11} stroke={2.4} /> Mailer enabled
            </span>
          </div>
        </div>

        {error && <div className={styles.error}>⚠ {error}</div>}

        <div className={styles.footer}>
          <button className={styles.cancelBtn} onClick={onClose}>Cancel</button>
          <button className={styles.saveBtn} onClick={handleSave} disabled={saving || extracting}>
            {saving ? 'Saving…' : extracting ? 'Reading email…' : 'Add to OnPoint'}
          </button>
        </div>
      </div>
    </div>
  );
}
